import type {
  ActionDef,
  AgendaDef,
  ContentPack,
  DecreeDef,
  DefId,
  IdentityDef,
  LocationDef,
  LocationId,
  ProblemDef,
  ResolutionDef,
  RuleCode,
  RulesOptions,
  SpellDef,
  SpellValues,
} from './types.js';

export interface ContentIndex {
  pack: ContentPack;
  locations: Map<LocationId, LocationDef>;
  spells: Map<DefId, SpellDef>;
  actions: Map<DefId, ActionDef>;
  problems: Map<DefId, ProblemDef>;
  identities: Map<DefId, IdentityDef>;
  agendas: Map<DefId, AgendaDef>;
  decrees: Map<DefId, DecreeDef>;
  resolutions: Map<DefId, ResolutionDef>;
}

const cache = new WeakMap<ContentPack, ContentIndex>();

export function indexContent(pack: ContentPack): ContentIndex {
  const hit = cache.get(pack);
  if (hit) return hit;
  const byId = <T extends { id: string }>(list: T[]) => new Map(list.map((d) => [d.id, d] as const));
  const idx: ContentIndex = {
    pack,
    locations: new Map(pack.locations.map((l) => [l.id, l] as const)),
    spells: byId(pack.spells),
    actions: byId(pack.actions),
    problems: byId(pack.problems),
    identities: byId(pack.identities),
    agendas: byId(pack.agendas),
    decrees: byId(pack.decrees ?? []),
    resolutions: byId(pack.resolutions ?? []),
  };
  cache.set(pack, idx);
  return idx;
}

export function spellDef(c: ContentIndex, id: DefId): SpellDef {
  const d = c.spells.get(id);
  if (!d) throw new Error(`Unknown spell ${id}`);
  return d;
}

export function actionDef(c: ContentIndex, id: DefId): ActionDef {
  const d = c.actions.get(id);
  if (!d) throw new Error(`Unknown action card ${id}`);
  return d;
}

export function problemDef(c: ContentIndex, id: DefId): ProblemDef {
  const d = c.problems.get(id);
  if (!d) throw new Error(`Unknown problem ${id}`);
  return d;
}

export function decreeDef(c: ContentIndex, id: DefId): DecreeDef {
  const d = c.decrees.get(id);
  if (!d) throw new Error(`Unknown decree ${id}`);
  return d;
}

export function resolutionDef(c: ContentIndex, id: DefId): ResolutionDef {
  const d = c.resolutions.get(id);
  if (!d) throw new Error(`Unknown resolution ${id}`);
  return d;
}

/** Is the rules option behind a proposal code on? (A Resolution's `requires`.) */
export function ruleOn(options: RulesOptions, code: RuleCode): boolean {
  switch (code) {
    case 'P-11':
      return !!options.precedenceOffice;
    case 'P-12':
      return options.internRound !== null && options.internRound !== undefined;
    case 'P-16':
      return options.arcaniumRefreshCost !== null && options.arcaniumRefreshCost !== undefined;
    case 'P-18':
      return !!options.annexRooms;
  }
}

/**
 * The Resolution a City Problem carries in a game with these options (P-17), or undefined: none
 * printed, Resolutions off, not in the enabled set, or a required option is off (removed at setup).
 */
export function activeResolution(c: ContentIndex, options: RulesOptions, problem: DefId): DefId | undefined {
  if (!options.resolutions) return undefined;
  const id = problemDef(c, problem).resolution;
  if (!id) return undefined;
  const def = c.resolutions.get(id);
  if (!def) return undefined;
  const set = options.resolutionSet ? c.pack.resolutionSets?.[options.resolutionSet] : null;
  if (set && !set.includes(id)) return undefined;
  return def.requires.every((code) => ruleOn(options, code)) ? id : undefined;
}

export function identityDef(c: ContentIndex, id: DefId): IdentityDef {
  const d = c.identities.get(id);
  if (!d) throw new Error(`Unknown identity ${id}`);
  return d;
}

export function locationDef(c: ContentIndex, id: LocationId): LocationDef {
  const d = c.locations.get(id);
  if (!d) throw new Error(`Unknown location ${id}`);
  return d;
}

/** Printed values for a spell under the active rules option (v2 default, or v1 for A/B). */
export function spellValues(c: ContentIndex, id: DefId, options: RulesOptions): SpellValues {
  const d = spellDef(c, id);
  if (options.cardValues === 'v1' && d.v1) {
    return { price: d.v1.price ?? d.price, cost: d.v1.cost ?? d.cost, vp: d.v1.vp ?? d.vp };
  }
  return { price: d.price, cost: d.cost, vp: d.vp };
}

export function isSpell(c: ContentIndex, def: DefId): boolean {
  return c.spells.has(def);
}
